import { useEffect, useRef, useState } from "react";
import { Pause, Play, RotateCcw } from "lucide-react";
import "./deal-walkthrough.css";

const STEPS = [
  {
    title: "Discover",
    detail: "Find a post that interests you and start a deal conversation.",
    alt: "Illustrated Busiconn product post with a protected payment badge and Deal button.",
  },
  {
    title: "Agree in chat",
    detail:
      "Ask questions and agree on the quantity, scope, and price together.",
    alt: "Illustrated buyer and seller conversation agreeing on two items for 420 rupees.",
  },
  {
    title: "Protect payment",
    detail:
      "The seller requests the agreed amount. Review the fees before paying; funds are held during the deal.",
    alt: "Illustrated payment request for 420 rupees with payment protection information.",
  },
  {
    title: "Review delivery",
    detail:
      "Track delivery in the same chat. Confirm completion or raise a dispute within the displayed review window.",
    alt: "Illustrated delivered deal tracker showing Raise Dispute and Confirm Completion actions.",
  },
];

export default function DealWalkthrough({
  theme,
}: {
  theme: "dark" | "light";
}) {
  const [step, setStep] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [visible, setVisible] = useState(false);
  const [pageVisible, setPageVisible] = useState(true);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(true);
  const container = useRef<HTMLDivElement>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const current = STEPS[step];
  const dark = theme === "dark";

  useEffect(() => {
    const preference = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReducedMotion(preference.matches);
    setPlaying(!preference.matches);
    const changed = () => {
      setReducedMotion(preference.matches);
      setPlaying(false);
    };
    preference.addEventListener("change", changed);
    const onVisibility = () =>
      setPageVisible(document.visibilityState === "visible");
    onVisibility();
    document.addEventListener("visibilitychange", onVisibility);
    const observer = new IntersectionObserver(
      ([entry]) => setVisible(entry.isIntersecting),
      { threshold: 0.25 }
    );
    if (container.current) observer.observe(container.current);
    // Cached prerendered images may load before React attaches onLoad.
    if (imageRef.current?.complete && imageRef.current.naturalWidth > 0)
      setReady(true);
    return () => {
      observer.disconnect();
      preference.removeEventListener("change", changed);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  useEffect(() => {
    if (!playing || reducedMotion || !visible || !pageVisible || !ready) return;
    const timer = window.setTimeout(() => {
      if (step === STEPS.length - 1) setPlaying(false);
      else setStep(step + 1);
    }, 4500);
    return () => window.clearTimeout(timer);
  }, [step, playing, reducedMotion, visible, pageVisible, ready]);

  function togglePlayback() {
    if (step === STEPS.length - 1 && !playing) {
      setStep(0);
      setPlaying(true);
    } else setPlaying((value) => !value);
  }

  return (
    <div
      ref={container}
      id="deal-walkthrough"
      className={`deal-walkthrough scroll-mt-24 rounded-3xl border p-4 sm:p-6 ${dark ? "bg-gray-900/80 border-gray-800" : "bg-gray-50 border-gray-200"}`}
      role="region"
      aria-label="How a Busiconn deal works">
      <div className="flex items-center justify-between gap-3 mb-4">
        <div>
          <p className="text-xs uppercase tracking-wider text-blue-500 font-semibold">
            Inside a Busiconn deal
          </p>
          <p
            className={`text-xs mt-1 ${dark ? "text-gray-400" : "text-gray-600"}`}>
            Illustrated from our app screens
          </p>
        </div>
        {!reducedMotion && (
          <button
            type="button"
            onClick={togglePlayback}
            disabled={!ready || failed}
            aria-label={
              playing
                ? "Pause walkthrough"
                : step === 3
                  ? "Replay walkthrough"
                  : "Play walkthrough"
            }
            className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-xs font-medium disabled:opacity-50 ${dark ? "border-gray-700 hover:bg-gray-800" : "border-gray-300 hover:bg-white"}`}>
            {playing ? (
              <Pause size={14} />
            ) : step === 3 ? (
              <RotateCcw size={14} />
            ) : (
              <Play size={14} />
            )}
            {playing ? "Pause" : step === 3 ? "Replay" : "Play"}
          </button>
        )}
      </div>
      <div
        className="deal-walkthrough-screen mx-auto rounded-2xl border border-gray-700 bg-white shadow-xl"
        role="img"
        aria-label={current.alt}>
        {!failed ? (
          <img
            ref={imageRef}
            src="/assets/images/landing/deal-walkthrough.webp"
            width="1774"
            height="887"
            alt=""
            aria-hidden="true"
            decoding="async"
            onLoad={() => setReady(true)}
            onError={() => {
              setFailed(true);
              setPlaying(false);
            }}
            className="deal-walkthrough-strip"
            style={{ transform: `translateX(-${step * 25}%)` }}
          />
        ) : (
          <p className="p-5 text-sm text-gray-700">
            Explore the deal steps below.
          </p>
        )}
      </div>
      <div
        className="grid grid-cols-4 gap-1.5 mt-5"
        aria-label="Walkthrough steps">
        {STEPS.map((item, index) => (
          <button
            key={item.title}
            type="button"
            aria-label={`Step ${index + 1}: ${item.title}`}
            aria-pressed={step === index}
            onClick={() => {
              setPlaying(false);
              setStep(index);
            }}
            className={`min-h-[44px] rounded-lg px-1.5 py-2 text-[11px] sm:text-xs leading-snug border ${step === index ? "bg-blue-600 border-blue-600 text-white" : dark ? "border-gray-700 text-gray-300 hover:bg-gray-800" : "border-gray-300 text-gray-700 hover:bg-white"}`}>
            <span className="block font-semibold mb-0.5">0{index + 1}</span>
            {item.title}
          </button>
        ))}
      </div>
      <div
        className="mt-4 min-h-[90px]"
        aria-live={playing ? "off" : "polite"}
        aria-atomic="true">
        <h2 className="font-semibold text-base">{current.title}</h2>
        <p
          className={`mt-1 text-sm leading-6 ${dark ? "text-gray-400" : "text-gray-600"}`}>
          {current.detail}
        </p>
      </div>
      <p
        className={`mt-2 text-[11px] leading-5 ${dark ? "text-gray-400" : "text-gray-600"}`}>
        Screens and amounts are illustrative. Payment release follows the deal
        terms; payout timing depends on provider confirmation.
      </p>
    </div>
  );
}
